import React, { useMemo, useState } from "react";
import { Crown } from "lucide-react";
import { vipFeeText } from "@/components/Seating";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, IdCard } from "lucide-react";
import { BookingDetailsForm } from "@/components/ProfileCards";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { evTitle } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { AppEvent } from "@/types";

export const optLabel = (o: { audience?: string | null; category?: string | null }, t: (k: string) => string) =>
  [o.audience && t(`aud.${o.audience}`), o.category && t(`cat.${o.category}`)].filter(Boolean).join(" · ");

const money = (amount: number, cur: string) => `${amount.toLocaleString("en-US", { minimumFractionDigits: cur === "USD" ? 2 : 0, maximumFractionDigits: cur === "USD" ? 2 : 0 })} ${cur}`;

export const BookingDialog = ({ event, onClose }: { event: AppEvent | null; onClose: () => void }) => {
  const { t, lang } = usePrefs();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [guests, setGuests] = useState(2);
  const [optionId, setOptionId] = useState("");
  const [seating, setSeating] = useState("");
  const [busy, setBusy] = useState(false);
  const key = useMemo(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`, [event]); // idempotency per dialog
  if (!event) return null;
  const opt = event.options?.find((o) => o.option_id === optionId);
  const unit = event.options?.length ? opt?.price : event.price;
  const seats = event.seating_types || [];
  const needOption = ((event.options?.length || 0) > 0 && !opt) || (seats.length > 0 && !seating);
  const vip = seating === "vip" && event.vip_fee ? event.vip_fee : null;
  const vipTotal = vip ? Number(vip.amount) * (event.vip_fee_per === "booking" ? 1 : guests) : 0;
  const cur = unit?.currency || vip?.currency;
  const total = (unit ? Number(unit.amount) * guests : 0) + vipTotal;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/bookings", { event_id: event.event_id, guests, option_id: optionId || undefined, seating: seating || undefined }, { headers: { "Idempotency-Key": key } });
      toast.success(t("bk.sent"));
      window.dispatchEvent(new Event("wn:notify"));
      onClose();
      navigate("/app/bookings");
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display text-xl">{evTitle(event, lang)}</DialogTitle></DialogHeader>
        {user && !user.booking_details_set ? (
          <div className="space-y-4" data-testid="booking-details-gate">
            <div className="flex gap-3 items-start rounded-2xl bg-primary/5 p-3">
              <IdCard className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-semibold">{t("bd.needed")}</p>
            </div>
            <BookingDetailsForm />
          </div>
        ) : (
          <div className="space-y-4">
            {!!event.options?.length && (
              <div className="space-y-2">
                <p className="text-sm font-bold">{t("bk.option")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {event.options.map((o) => (
                    <button key={o.option_id} type="button" onClick={() => setOptionId(o.option_id)} data-testid={`booking-option-${o.option_id}`}
                      className={`rounded-2xl border p-3 text-start transition-[background-color] ${optionId === o.option_id ? "bg-foreground text-background" : "bg-card hover:bg-accent"}`}>
                      <p className="text-sm font-bold">{optLabel(o, t)}</p>
                      <p className="text-xs opacity-80" dir="ltr">{money(Number(o.price.amount), o.price.currency)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {seats.length > 0 && (
              <div className="space-y-2" data-testid="booking-seating">
                <p className="text-sm font-bold">{t("seat.choose")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {seats.map((s) => (
                    <button key={s} type="button" onClick={() => setSeating(s)} aria-pressed={seating === s} data-testid={`booking-seating-${s}`}
                      className={`rounded-2xl border p-3 text-start transition-[background-color] ${seating === s ? (s === "vip" ? "bg-amber-400 text-amber-950 border-amber-400" : "bg-foreground text-background") : "bg-card hover:bg-accent"}`}>
                      <p className="text-sm font-bold inline-flex items-center gap-1">{s === "vip" && <Crown className="h-4 w-4" />}{t(`seat.${s}`)}</p>
                      {s === "vip" && event.vip_fee && <p className="text-xs opacity-80" dir="ltr">{vipFeeText(event.vip_fee, event.vip_fee_per, t)}</p>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">{t("bk.people")}</p>
              <div className="flex items-center gap-3">
                <button type="button" data-testid="booking-guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className="h-10 w-10 rounded-full border grid place-items-center"><Minus className="h-4 w-4" /></button>
                <span data-testid="booking-guests-value" className="w-6 text-center font-black text-lg">{guests}</span>
                <button type="button" data-testid="booking-guests-plus" onClick={() => setGuests(Math.min(50, guests + 1))} className="h-10 w-10 rounded-full border grid place-items-center"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="rounded-2xl bg-accent/60 p-4 space-y-1 text-sm" data-testid="booking-review">
              <p className="font-bold">{t("bk.review")}</p>
              <p>{t(`ev.pricing.${event.pricing_model}`)}</p>
              {seating && <p className="flex justify-between"><span>{t("seat.type")}</span><b data-testid="booking-seating-review">{t(`seat.${seating}`)}</b></p>}
              {vip && <p className="flex justify-between"><span>{t("seat.vipFee")}</span><b dir="ltr" data-testid="booking-vip-fee">{money(vipTotal, vip.currency)}</b></p>}
              {cur && <p className="flex justify-between"><span>{t("bk.total")}</span><b dir="ltr" data-testid="booking-total">{money(total, cur)}</b></p>}
              {event.price_includes && <p className="text-xs text-muted-foreground">{t("ev.includes")}: {event.price_includes}</p>}
              {event.terms && <p className="text-xs text-muted-foreground">{t("ev.terms")}: {event.terms}</p>}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="booking-request-hint">{t("b5.requestHint")}</p>
            <button data-testid="booking-submit-button" disabled={busy || needOption} onClick={submit} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">
              {busy ? t("common.loading") : t("ev.request")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
